import { apiSlice } from "shared/api";

import { isSubmissionStatusForward } from "../model/submissionStatus";
import type {
    AdminSubmissionItem,
    SubmissionDetail,
    SubmissionItem,
    SubmitCodeRequestData,
} from "../model/types";

interface SubmissionsQueryArg {
    duelId: string;
    taskKey?: string | null;
}

const normalizeSubmissionsArg = (arg: string | SubmissionsQueryArg) => {
    if (typeof arg === "string") return { duelId: arg, taskKey: null };
    return arg;
};

export const submitCodeApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        submitCode: builder.mutation<
            SubmissionDetail,
            { duelId: string; data: SubmitCodeRequestData; taskKey?: string | null }
        >({
            query: ({ duelId, data }) => ({
                url: `/duels/${duelId}/submissions`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: () => [],
            async onQueryStarted({ duelId, data, taskKey }, { dispatch, queryFulfilled }) {
                try {
                    const { data: result } = await queryFulfilled;
                    dispatch(
                        submitCodeApiSlice.util.updateQueryData(
                            "getSubmissions",
                            { duelId, taskKey: taskKey ?? null },
                            (draft) => {
                                const exists = draft.some(
                                    (submission) =>
                                        String(submission.submission_id) === String(result.id),
                                );
                                if (exists) return;
                                draft.unshift({
                                    submission_id: result.id,
                                    status: result.status ?? "Queued",
                                    language: result.language ?? data.language,
                                    created_at: result.created_at ?? new Date().toISOString(),
                                    message: result.message ?? null,
                                    verdict: result.verdict ?? null,
                                    is_upsolving: result.is_upsolving ?? false,
                                });
                            },
                        ),
                    );
                } catch (error) {
                    console.debug("submitCode onQueryStarted failed", error);
                }
            },
        }),

        getSubmissions: builder.query<SubmissionItem[], string | SubmissionsQueryArg>({
            query: (arg) => {
                const { duelId, taskKey } = normalizeSubmissionsArg(arg);
                return {
                    url: `/duels/${duelId}/submissions`,
                    ...(taskKey ? { params: { taskKey } } : {}),
                };
            },
            providesTags: (result, _error, arg) => {
                const { duelId } = normalizeSubmissionsArg(arg);
                return result
                    ? [
                          ...result.map(({ submission_id }) => ({
                              type: "Submission" as const,
                              id: `${duelId}-${submission_id}`,
                          })),
                          { type: "Submission", id: `LIST-${duelId}` },
                      ]
                    : [{ type: "Submission", id: `LIST-${duelId}` }];
            },
            merge: (currentCache, newItems) => {
                if (!currentCache) return newItems;
                const resultMap = new Map<string, SubmissionItem>();
                currentCache.forEach((item) => {
                    resultMap.set(String(item.submission_id), item);
                });
                newItems.forEach((item) => {
                    const submissionId = String(item.submission_id);
                    const current = resultMap.get(submissionId);
                    if (!current || isSubmissionStatusForward(current.status, item.status)) {
                        resultMap.set(submissionId, item);
                    }
                });
                return Array.from(resultMap.values());
            },
            forceRefetch: ({ currentArg, previousArg }) => {
                if (!currentArg || !previousArg) return true;
                const current = normalizeSubmissionsArg(currentArg);
                const previous = normalizeSubmissionsArg(previousArg);
                return current.duelId !== previous.duelId || current.taskKey !== previous.taskKey;
            },
        }),

        getSubmissionDetail: builder.query<
            SubmissionDetail,
            { duelId: string; submissionId: string }
        >({
            query: ({ duelId, submissionId }) => ({
                url: `/duels/${duelId}/submissions/${submissionId}`,
            }),
            providesTags: (_result, _error, { duelId, submissionId }) => [
                { type: "Submission", id: `${duelId}-${submissionId}` },
            ],
            merge: (currentCache, incoming) => {
                if (!isSubmissionStatusForward(currentCache.status, incoming.status)) return;
                Object.assign(currentCache, incoming);
            },
            async onQueryStarted({ duelId, submissionId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        submitCodeApiSlice.util.updateQueryData(
                            "getSubmissions",
                            { duelId, taskKey: null },
                            (draft) => {
                                const index = draft.findIndex(
                                    (submission) =>
                                        String(submission.submission_id) === String(submissionId),
                                );
                                if (index === -1) return;
                                if (!isSubmissionStatusForward(draft[index].status, data.status)) {
                                    return;
                                }
                                draft[index] = {
                                    ...draft[index],
                                    status: data.status,
                                    message: data.message,
                                    verdict: data.verdict,
                                };
                            },
                        ),
                    );
                } catch (error) {
                    console.debug("getSubmissionDetail onQueryStarted failed", error);
                }
            },
        }),
        getAdminTestingSubmissions: builder.query<AdminSubmissionItem[], void>({
            query: () => "/duels/admin/submissions/testing",
        }),
        getAdminSubmissions: builder.query<AdminSubmissionItem[], void>({
            query: () => "/duels/admin/submissions/all",
        }),
    }),
});

export const {
    useSubmitCodeMutation,
    useGetSubmissionsQuery,
    useGetSubmissionDetailQuery,
    useGetAdminTestingSubmissionsQuery,
    useGetAdminSubmissionsQuery,
} = submitCodeApiSlice;
